import React, { useContext, useEffect, useState, useCallback } from "react";
import LogoAuthNavbar from "./LogoAuthNavbar";
import { ReactComponent as OrderIcon } from "../assets/icons/Orders.svg";
import { ReactComponent as QuoteIcon } from "../assets/icons/Quotes.svg";
import { ReactComponent as TeamIcon } from "../assets/icons/TeamMembers.svg";
import { ReactComponent as InvocieIcon } from "../assets/icons/Invoices.svg";
import { ReactComponent as ProfileIcon } from "../assets/icons/MyProfile.svg";
import { ReactComponent as AccountIcon } from "../assets/icons/AccountSettings.svg";
import { ReactComponent as DashboardIcon } from "../assets/icons/Dashboard.svg";
import { ReactComponent as EmployeesIcon } from "../assets/icons/Employees.svg";
import { ReactComponent as CouponsIcon } from "../assets/icons/Coupons.svg";
import { TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import { TbLayoutSidebarLeftExpand } from "react-icons/tb";
import { ReactComponent as NotificationsIcon } from "../assets/icons/Notifications.svg"
import { ImBlocked } from "react-icons/im";
import { useSpring, animated, config } from 'react-spring';
import { useScreenTransition } from "../Hooks/useScreenTransition";
import { DataContext } from "../Context/DataContext";
import { AuthContext } from "../Context/AuthContext";

import { firestore } from "../firebase.config";
import { Menu, Shield } from "react-feather";
import { AccountType } from "../utility/enums";
import { INVOICESTATUS, ORDERSTATUS, QUOTESTATUS } from "../Data/statuses";

const AccountNavBar = ({ windowSize, children, selectedTab = "profile", showButton = true, accountMainPadding = true, bgTransparent = false, breadLink = null, breadLinkType, extraBreadLink = false, extraBreadLinkitem, extraBreadPath, admin = false }) => {

  const { userData, teamData } = useContext(DataContext);
  const { signedIn } = useContext(AuthContext);

  const phoneScreen = windowSize.width > 767 || windowSize.orientation === 'landscape' ? false : true;

  const [menuOpen, setMenuOpen] = useState(!phoneScreen);

  const accountMenuProps = useSpring({ maxHeight: menuOpen || !phoneScreen ? !admin ? '317px' : '375px' : '50px', config: config.slow });

  const { transitionTo } = useScreenTransition();

  const [orderCount, setOrderCount] = useState("");
  const [quoteCount, setQuoteCount] = useState("");
  const [invoiceCount, setInvoiceCount] = useState("");
  const [collapse, setCollapse] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapse");
    const screenWidth = window.innerWidth;
    if (saved !== null) {
      return saved === "true";
    }
    if (screenWidth >= 1024) {
      localStorage.setItem("sidebarCollapse", "false");
      return false;
    }
    if (screenWidth >= 768 && screenWidth < 1024) {
      localStorage.setItem("sidebarCollapse", "true");
      return true;
    }
    return false;
  });

  let targetWidth = collapse ? 44 : 200;

  const sidebarSpring = useSpring({
    width: targetWidth,
    config: config.slow,
  });

  const sidebarTextSpring = useSpring({
    opacity: phoneScreen ? 1 : collapse ? 0 : 1,
    maxWidth: phoneScreen ? 200 : collapse ? 0 : 200,
    config: { ...config.default, tension: 300, friction: 30 },
  });

  useEffect(() => {
    if (signedIn && !localStorage.getItem("sidebarCollapse")) {
      const screenWidth = window.innerWidth;

      if (screenWidth >= 1024) {
        setCollapse(false);
        localStorage.setItem("sidebarCollapse", "false");
      }
      else if (screenWidth >= 768 && screenWidth < 1024) {
        setCollapse(true);
        localStorage.setItem("sidebarCollapse", "true");
      }
      else {
        setCollapse(false);
      }
    }
  }, [signedIn]);

  const checkCount = (count) => {
    if (count > 99) {
      return "99+";
    }
    return count;
  }

  const getOrderCount = async (orderUnSub) => {
    const orderRef = firestore.collection("orders").where('status', 'in', ['new', 'in-progress', 'support-review', 'revision', 'on-hold']);
    const orderQuery = userData.role === AccountType.OWNER && admin ? orderRef : orderRef.where(userData.teamId ? "teamId" : "userID", "==", userData.teamId ? userData.teamId : userData.id);

    orderUnSub = orderQuery.onSnapshot((oSnap => {
      if (oSnap.empty) {
        setOrderCount("");
        return;
      }

      const ordersData = oSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      const newOrders = ordersData.filter(o => o.status === "new");
      const inProgess = ordersData.filter(o => o.status === "in-progress");
      const qualityCheck = ordersData.filter(o => o.status === "support-review");
      const revision = ordersData.filter(o => o.status === "revision");
      const onHold = ordersData.filter(o => o.status === "on-hold" && !o.cancellationHold);
      const onCancelHold = ordersData.filter(o => o.status === "on-hold" && o.cancellationHold);
      const count = {
        new: newOrders.length ? checkCount(newOrders.length) : "",
        inProgess: inProgess.length ? checkCount(inProgess.length) : "",
        qualityCheck: qualityCheck.length ? checkCount(qualityCheck.length) : "",
        revision: revision.length ? checkCount(revision.length) : "",
        onHold: onHold.length > 0 ? checkCount(onHold.length) : "",
        onCancelHold: onCancelHold.length > 0 ? checkCount(onCancelHold.length) : ""
      }
      setOrderCount(count);

    }));

  }

  const getQuoteCount = async (quoteUnSub) => {
    const quoteRef = firestore.collection("quotes").where('status', '==', 'in-review');
    const quoteQuery = userData.role === AccountType.OWNER && admin ? quoteRef : quoteRef.where(userData.teamId ? "teamId" : "userID", "==", userData.teamId ? userData.teamId : userData.id);

    quoteUnSub = quoteQuery.onSnapshot((qSnap) => {

      if (qSnap.empty) {
        setQuoteCount("");
        return;
      }
      setQuoteCount(checkCount(qSnap.docs.length));

    });

  }

  const getInvoiceCount = async (invoiceUnSub) => {
    const invoiceRef = firestore.collection("orderInvoices").where('status', 'in', ['overdue', 'pay-later', 'on-hold']);
    const invoiceQuery = userData.role === AccountType.OWNER && admin ? invoiceRef : invoiceRef.where(userData.teamId ? "teamId" : "userID", "==", userData.teamId ? userData.teamId : userData.id);

    invoiceUnSub = invoiceQuery.onSnapshot((iSnap) => {
      if (iSnap.empty) {
        setInvoiceCount("");
        return;
      }
      const invoiceData = iSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      const overdue = invoiceData.filter(o => o.status === "overdue");
      const waitingPayment = invoiceData.filter(o => o.status === "pay-later");
      const onHold = invoiceData.filter(o => o.status === "on-hold");
      const count = {
        overdue: overdue.length ? checkCount(overdue.length) : "",
        waitingPayment: waitingPayment.length ? checkCount(waitingPayment.length) : "",
        onHold: onHold.length ? checkCount(onHold.length) : "",
      }

      setInvoiceCount(count);

    });
  }

  useEffect(() => {

    if (userData && signedIn) {
      if (userData.teamId && teamData) {
        if (teamData.newTeam && (userData.teamRole === AccountType.OWNER || userData.teamRole === AccountType.ADMIN)) {
          transitionTo("/account/onboarding")
          return;
        }
      }
    }
  }, [userData, signedIn, teamData])

  useEffect(() => {
    let invoiceUnSub = null;
    let orderUnSub = null;
    let quoteUnSub = null;

    if (signedIn && userData) {
      getOrderCount(orderUnSub);
      getQuoteCount(quoteUnSub);
      getInvoiceCount(invoiceUnSub);
    }

    return () => {
      if (invoiceUnSub) invoiceUnSub();
      if (orderUnSub) orderUnSub();
      if (quoteUnSub) quoteUnSub();
    }
  }, [signedIn, userData, teamData])

  const OrderCountComponent = ({ orderCount }) => {
    return (
      <>
        {orderCount && <animated.div className="accountSideBarSecondWrapper accountSidebarText">
          {orderCount.new && <div className="accountSideBarSecond" style={{
          backgroundColor: ORDERSTATUS["new"][1] }}>
            <p style={{ color: "#2b2b2b" }}>{orderCount.new}</p>
          </div>}
          {orderCount.inProgess && <div className="accountSideBarSecond" style={{ backgroundColor: ORDERSTATUS["in-progress"][1] }}>
            <p>{orderCount.inProgess}</p>
          </div>}
          {orderCount.qualityCheck && <div className="accountSideBarSecond" style={{ backgroundColor: ORDERSTATUS["support-review"][1] }}>
            <p >{orderCount.qualityCheck}</p>
          </div>}
          {orderCount.revision && <div className="accountSideBarSecond" style={{ backgroundColor: ORDERSTATUS["revision"][1] }}>
            <p>{orderCount.revision}</p>
          </div>}
          {orderCount.onHold && <div className="accountSideBarSecond" style={{ backgroundColor: ORDERSTATUS["on-hold"][1] }}>
            <p style={{ color: "#2b2b2b" }}>{orderCount.onHold}</p>
          </div>}
          {orderCount.onCancelHold && <div className="accountSideBarSecond" style={{ backgroundColor: ORDERSTATUS["cancel-hold"][1] }}>
            <p style={{ color: "#2b2b2b" }}>{orderCount.onCancelHold}</p>
          </div>}
        </animated.div>}
      </>
    )
  }

  const InvoiceCountComponent = ({ invoiceCount }) => {
    return (
      <>
        {invoiceCount && <animated.div className="accountSideBarSecondWrapper accountSidebarText">
          {invoiceCount.waitingPayment && <div className="accountSideBarSecond" style={{ backgroundColor: INVOICESTATUS["pay-later"][1] }}>
            <p style={{ color: "#2b2b2b" }}>{invoiceCount.waitingPayment}</p>
          </div>}
          {invoiceCount.overdue && <div className="accountSideBarSecond" style={{ backgroundColor: INVOICESTATUS["overdue"][1] }}>
            <p>{invoiceCount.overdue}</p>
          </div>}
          {invoiceCount.onHold && <div className="accountSideBarSecond" style={{ backgroundColor: INVOICESTATUS["on-hold"][1] }}>
            <p style={{ color: "#2b2b2b" }}>{invoiceCount.onHold}</p>
          </div>}
        </animated.div>}
      </>
    )
  }

  const handleSidebarCollapseExpand = useCallback(() => {
    const newCollapse = !collapse;
    setCollapse(newCollapse);
    localStorage.setItem("sidebarCollapse", String(newCollapse));
  }, [collapse]);


  return (
    <div className="homeContainer md:px-6 px-4">
      <LogoAuthNavbar
        showButtons={showButton}
        extraBreadLink={extraBreadLink}
        extraBreadLinkitem={extraBreadLinkitem}
        extraBreadPath={extraBreadPath}
        accountPages={true}
        breadLink={breadLink}
        breadLinkType={breadLinkType}
        admin={admin}
      />
      {
        !signedIn && !userData ? (
          <div className="accountContainer">
            <animated.div
              style={{ ...accountMenuProps, ...sidebarSpring }}
              className="accountSidebar select-none"
            >
              {phoneScreen && (
                <div
                  className="accountSidebarTitle mb-4"
                  onClick={() => setMenuOpen((i) => !i)}
                >
                  Account Menu
                </div>
              )}
              <div
                className={`accountSidebarWrap ${selectedTab === "orders" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <OrderIcon className="accountSideBarIcon" />
                  <animated.p
                    className="accountSidebarLink accountSidebarText"
                    style={sidebarTextSpring}
                  >
                    Home
                  </animated.p>
                </div>
              </div>
            </animated.div>

            <div className={`accountMainContainer ${bgTransparent ? "bgTransparent" : ""} ${accountMainPadding ? "p-5" : "p-0"}`}>
              {children}
            </div>
          </div>
        ) : !admin ? (
          <div className="accountContainer">
            <animated.div
              style={{ ...accountMenuProps, ...sidebarSpring }}
              className="accountSidebar"
            >
              {phoneScreen && (
                <div
                  className="accountSidebarTitle mb-4 flex justify-center items-center gap-1"
                  onClick={() => setMenuOpen((i) => !i)}
                >
                  <Menu width={"20px"} height={"20px"} />
                  Account Menu
                </div>
              )}
              <div
                onClick={handleSidebarCollapseExpand}
                data-label={collapse ? "Expand" : "Collapse"}
                className="md:block hidden p-3 cursor-pointer collapseExpand"
                aria-expanded={collapse}
              >
                {collapse ? (
                  <TbLayoutSidebarLeftExpand color="#9A9FA7" size={20} />
                ) : (
                  <TbLayoutSidebarLeftCollapse color="#9A9FA7" size={20} />
                )}
              </div>

              {/* Orders */}
              <div
                data-label="Orders"
                className={`accountSidebarWrap ${selectedTab === "orders" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/orders", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <OrderIcon className="accountSideBarIcon" />
                  <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Orders
                  </animated.p>
                </div>
                <OrderCountComponent orderCount={orderCount} />
              </div>

              {/* Quotes */}
              <div
                data-label="Quotes"
                className={`accountSidebarWrap ${selectedTab === "quotes" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/quotes", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <QuoteIcon className="accountSideBarIcon" />
                  <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Quotes
                  </animated.p>
                </div>
                <div className="accountSideBarSecondWrapper accountSidebarText">
                  {quoteCount > 0 && (
                    <div className="accountSideBarSecond">
                      <p>{quoteCount}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Team Members */}
              {signedIn && userData && userData.teamId && (
                <div
                  data-label="Team Members"
                  className={`accountSidebarWrap ${selectedTab === "team-members" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/account/team-members", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <TeamIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Team Members
                    </animated.p>
                  </div>
                </div>
              )}

              {/* Invoices */}
              <div
                data-label="Invoices"
                className={`accountSidebarWrap ${selectedTab === "invoices" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/invoices", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <InvocieIcon className="accountSideBarIcon" />
                  <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Invoices
                  </animated.p>
                </div>
                <InvoiceCountComponent invoiceCount={invoiceCount} />
              </div>

              {/* Profile */}
              <div
                data-label="My Profile"
                className={`accountSidebarWrap ${selectedTab === "profile" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/profile", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <ProfileIcon className="accountSideBarIcon" />
                  <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    My Profile
                  </animated.p>
                </div>
              </div>

              {/* Settings */}
              <div
                data-label="Account Settings"
                className={`accountSidebarWrap ${selectedTab === "settings" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/settings", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <AccountIcon className="accountSideBarIcon" />
                  <animated.h2 className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Account Settings
                  </animated.h2>
                </div>
              </div>

              {/* Owner Portal */}
              {userData && userData.role === "owner" && (
                <div
                  data-label="Owner Portal"
                  className={`accountSidebarWrap ${selectedTab === "owner-portal" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/translations", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <Shield className="accountSideBarIcon" color="#F0F0F0" />
                    <animated.h2 className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Owner Portal
                    </animated.h2>
                  </div>
                </div>
              )}
            </animated.div>

            <div className={`accountMainContainer ${bgTransparent ? "bgTransparent" : ""} ${accountMainPadding ? "p-5" : "p-0"}`}>
              {children}
            </div>
          </div>
        ) : (
          <div className="accountContainer">
            <animated.div style={{ ...sidebarSpring }} className="accountSidebar select-none">
              {phoneScreen && (
                <div
                  className="accountSidebarTitle mb-4"
                  onClick={() => setMenuOpen((i) => !i)}
                >
                  Account Menu
                </div>
              )}
              <div
                onClick={handleSidebarCollapseExpand}
                data-label={collapse ? "Expand" : "Collapse"}
                className="md:block hidden ml-[12px] mb-1.5 cursor-pointer collapseExpand"
                aria-expanded={collapse}
              >
                {collapse ? (
                  <TbLayoutSidebarLeftExpand size={20} color="#9A9FA7" />
                ) : (
                  <TbLayoutSidebarLeftCollapse size={20} color="#9A9FA7" />
                )}
              </div>

              {/* Dashboard */}
              {signedIn && userData && (userData.role !== "Support" || userData.role !== "SupportPlus") && (
                <div
                  data-label="Dashboard"
                  className={`accountSidebarWrap ${selectedTab === "dashboard" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/dashboard", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <DashboardIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Dashboard
                    </animated.p>
                  </div>
                </div>
              )}

              {/* Orders */}
              <div
                data-label="Orders"
                className={`accountSidebarWrap ${selectedTab === "orders" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/office/translations", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <OrderIcon className="accountSideBarIcon" />
                  <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Orders
                  </animated.p>
                </div>
                <OrderCountComponent orderCount={orderCount} />
              </div>

              {/* Quotes */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Quotes"
                  className={`accountSidebarWrap ${selectedTab === "quotes" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/quotes", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <QuoteIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Quotes
                    </animated.p>
                  </div>
                  {quoteCount && (
                    <div className="accountSideBarSecondWrapper accountSidebarText">
                      <div className="accountSideBarSecond" style={{ backgroundColor: QUOTESTATUS["in-review"][1] }}>
                        <p style={{ color: "#2b2b2b" }}>{quoteCount}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Invoices */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Invoices"
                  className={`accountSidebarWrap ${selectedTab === "invoices" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/invoices", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <InvocieIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Invoices
                    </animated.p>
                  </div>
                  <InvoiceCountComponent invoiceCount={invoiceCount} />
                </div>
              )}

              {/* Employees */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Employees"
                  className={`accountSidebarWrap ${selectedTab === "employees" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/employees", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <EmployeesIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Employees
                    </animated.p>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Notifications"
                  className={`accountSidebarWrap ${selectedTab === "admin-notifications" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/admin-notifications", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <NotificationsIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Notifications
                    </animated.p>
                  </div>
                </div>
              )}

              {/* Teams */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Teams"
                  className={`accountSidebarWrap ${selectedTab === "teams" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/teams", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <TeamIcon className="accountSideBarIcon" />
                    <animated.p className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Teams
                    </animated.p>
                  </div>
                </div>
              )}

              {/* Coupons */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Coupons"
                  className={`accountSidebarWrap ${selectedTab === "coupons" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/coupons", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <CouponsIcon className="accountSideBarIcon" />
                    <animated.h2 className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Coupons
                    </animated.h2>
                  </div>
                </div>
              )}

              {/* Blocked Users */}
              {signedIn && userData && userData.role !== "Support" && (
                <div
                  data-label="Blocked Users"
                  className={`accountSidebarWrap ${selectedTab === "blockedUsers" && "selectedAccountSideBar"}`}
                  onClick={(e) => transitionTo("/office/blocked-users", e)}
                  aria-expanded={collapse}
                >
                  <div className="accountSideBarFirst">
                    <ImBlocked className="accountSideBarIcon" />
                    <animated.h2 className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                      Blocked Users
                    </animated.h2>
                  </div>
                </div>
              )}

              {/* Account Setting */}
              <div
                data-label="Account Setting"
                className={`accountSidebarWrap ${selectedTab === "owner-portal" && "selectedAccountSideBar"}`}
                onClick={(e) => transitionTo("/account/settings", e)}
                aria-expanded={collapse}
              >
                <div className="accountSideBarFirst">
                  <AccountIcon className="accountSideBarIcon" />
                  <animated.h2 className="accountSidebarLink accountSidebarText" style={sidebarTextSpring}>
                    Account Setting
                  </animated.h2>
                </div>
              </div>
            </animated.div>

            <div className={`accountMainContainer ${bgTransparent ? "bgTransparent" : ""} ${accountMainPadding ? "p-5" : "p-0"}`}>
              {children}
            </div>
          </div>
        )
      }
    </div>
  );
}
export default AccountNavBar;
